import { forwardRef } from "react"
import type { ForwardRefExoticComponent, ForwardedRef, ReactElement } from "react"

import { AnimatePresence, m } from "framer-motion"

import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { CheckMarkIcon } from "../../../assets"
import { ETypographyColor, ETypographySize, ETypographyTag, Typography } from "../typography"

type TProps = { isCompleted?: boolean; isSaving?: boolean; isRequired?: boolean; wrapperClassName?: string }

const classes = {
    wrapper: "absolute top-auto left-auto right-0 bottom-0 flex items-center",
    holder: "pt-[4px] pr-[12px] pb-[6px] pl-[5px] flex items-center gap-x-[3px] select-none",
    markWrapper: "flex items-center justify-center w-[18px] h-[18px]",
    mark: "border-gray-23 border-dashed border-[1px] rounded-full h-[10px] w-[10px]"
}

const Component: ForwardRefExoticComponent<TProps> = forwardRef(
    (
        { isCompleted, isSaving = false, isRequired = false, wrapperClassName = String() }: TProps,
        ref: ForwardedRef<HTMLDivElement>
    ): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        return (
            <div
                ref={ref}
                className={buildCssClassName(classes.wrapper, {
                    [wrapperClassName]: wrapperClassName
                })}
            >
                <AnimatePresence mode="wait" initial={false}>
                    {isCompleted ? (
                        <m.div
                            key="completed"
                            className={classes.holder}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <CheckMarkIcon />
                            <Typography
                                text={t("common.complete")}
                                tag={ETypographyTag.Span}
                                color={ETypographyColor.Gray23}
                                size={ETypographySize.Tiny}
                            />
                        </m.div>
                    ) : (
                        <m.div
                            key={isSaving ? "saving" : isRequired ? "required" : "incomplete"}
                            className={classes.holder}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <div className={classes.markWrapper}>
                                <i className={classes.mark} />
                            </div>
                            <Typography
                                text={
                                    isSaving
                                        ? t("common.saving")
                                        : isRequired
                                          ? t("common.required")
                                          : t("common.incomplete")
                                }
                                isItalic
                                tag={ETypographyTag.Span}
                                color={ETypographyColor.Gray23}
                                size={ETypographySize.Tiny}
                            />
                        </m.div>
                    )}
                </AnimatePresence>
            </div>
        )
    }
)

Component.displayName = "CompletionMark"

export { Component as CompletionMark }
